import { ColDef } from 'ag-grid-community';
import { camelCase } from 'lodash';
import { UseMutateFunction } from 'react-query';
import { Project } from 'api/project';
import {
  asColumnOrder,
  ColumnOrder,
} from 'components/organisms/grid-custom-column';
import { getEditor } from 'components/organisms/tasks/editors';
import { FilterableField } from 'components/organisms/tasks/fields';
import { STATUS_FIELD_NAME } from 'data/fields/hooks';
import { ProjectGridPayload } from 'queries/project';
import { TaskField } from 'stores/fields';
import { ProjectFieldsName } from '../fields';
import { getGridUpdater } from './cell-editors';
import { getComparator } from './comparators';
import { getHeaderName } from './get-header-name';
import { getValueFormatter } from './get-value-formatter';
import { getKeyCreator } from './key-creator';
import { getRenderer } from './renderer';
import { getValueGetter } from './value-getter';

const relativeSortColumns = (
  source: ColDef[],
  target: (string | boolean)[][],
): ColDef[] => {
  const order = Object.fromEntries(
    target.map((v, i) => [v[0], { order: i, hide: v[1] }]),
  );

  return Array.from(source)
    .map((x) => ({ ...x, hide: order[x.field!]?.hide ?? false }))
    .sort(
      (a, b) =>
        (order[a.field!]?.order ?? Number.MAX_VALUE) -
        (order[b.field!]?.order ?? Number.MAX_VALUE),
    );
};

const defaultColumnOrder = [
  'projectName',
  'status',
  'currentPhases',
  'progressPercent',
  'projectPhases',
  'dueDate',
  'inferredProgress',
  'estimatedDueDate',
  'pastProjectStatus',
  'projectHours',
  'projectOwner',
  'arr',
  'projectFee',
  'csatRating',
  'lastMilestoneCSAT',
  'startDate',
  'createdAt',
  'customerChampion',
  'customerMembersInvited',
  'customerMembersAccepted',
];

export const stringToColumnOrder = (colId: string): ColumnOrder => ({
  colId,
  hide: false,
  sort: null,
  sortIndex: null,
  pinned: colId === 'projectName' ? 'left' : undefined,
  width: colId === 'projectName' ? 360 : 200,
});
export const DEFAULT_COLUMN_ORDER: ColumnOrder[] =
  defaultColumnOrder.map(stringToColumnOrder);

const withEditFields = (field: FilterableField): ColDef => {
  const editableFields = [
    ProjectFieldsName.startDate,
    ProjectFieldsName.dueDate,
    ProjectFieldsName.status,
  ];

  const editable =
    field.fieldType === 'KEY_EVENTS'
      ? field.metaData?.allowAdminsAndOwnersToEdit ?? true
      : field.fieldType !== 'RATING' &&
        field.fieldType !== 'INTERVALS' &&
        (field.userCreated || editableFields.includes(field.fieldName));

  const cellEditorFramework = editable ? getEditor(field as TaskField) : null;
  const cellClass = editable ? ['grid-cell-editable'] : [];

  return {
    editable,
    cellClass,
    cellEditorFramework,
  };
};

const FIRST_COLUMN = defaultColumnOrder[0];

const toColumnDef = (field: FilterableField): ColDef => {
  return {
    field:
      camelCase(field.fieldName) === camelCase(STATUS_FIELD_NAME)
        ? 'status'
        : field.fieldName,
    headerName: getHeaderName(field),
    headerValueGetter: (params) => {
      if (params.colDef.field === 'projectName') {
        const gridApi = params.api;
        const totalRowCount = gridApi.getDisplayedRowCount();
        const groupCount = gridApi.getModel().getTopLevelRowCount();
        let dataRowCount = totalRowCount;

        if (totalRowCount !== groupCount) {
          dataRowCount = totalRowCount - groupCount;
        }

        return `${getHeaderName(field)} (${dataRowCount})`;
      }

      return getHeaderName(field);
    },
    /* TODO:: Fix translation */
    headerTooltip: [
      ProjectFieldsName.inferredProgress,
      ProjectFieldsName.estimatedDueDate,
    ].includes(field.fieldName)
      ? 'Smart estimation of how well the project is progressing as per the plan.'
      : undefined,
    cellRenderer: getRenderer(field),
    valueGetter: getValueGetter(field),
    valueFormatter: getValueFormatter(field),
    keyCreator: getKeyCreator(field),
    comparator: getComparator(field),
    sortable: ![
      ProjectFieldsName.pastProjectStatus,
      ProjectFieldsName.projectPhases,
    ].includes(field.fieldName),
    lockPosition: field.fieldName === FIRST_COLUMN,
    pinned: field.fieldName === FIRST_COLUMN,
    suppressColumnsToolPanel: field.fieldName === FIRST_COLUMN,
    minWidth: field.fieldName === FIRST_COLUMN ? 360 : undefined,
    cellStyle: (params) => {
      if (params.colDef.field === 'projectName') {
        return { borderRight: '1px solid var(--table-border-color)' };
      }

      return undefined;
    },
  };
};

export const getColDef = (fields: FilterableField[]): ColDef[] =>
  fields.map((field) => ({
    ...toColumnDef(field),
    ...withEditFields(field),
  }));

export const relativelySortByDefaultColumnOrder = (
  coldDefs: ColDef[],
): ColDef[] => {
  const newColumnOrder = defaultColumnOrder.map((x) => [x, false]);

  return relativeSortColumns(coldDefs, newColumnOrder);
};

export const getDefaultOrderedColumns = (colDefs: ColDef[]): ColumnOrder[] => {
  return asColumnOrder(
    relativelySortByDefaultColumnOrder(colDefs).map((col) => ({
      ...col,
      colId: col.field,
    })),
  );
};

const toEditParam =
  (mutate: UseMutateFunction<Project, unknown, ProjectGridPayload, unknown>) =>
  (def: ColDef): ColDef => ({
    ...def,
    cellEditorParams: {
      onUpdate: getGridUpdater(mutate),
    },
  });

export const withOnUpdate =
  (mutate: UseMutateFunction<Project, unknown, ProjectGridPayload, unknown>) =>
  (defs: ColDef[]): ColDef[] =>
    defs.map(toEditParam(mutate));

export const withRowGroup =
  (groupBy: string) =>
  (colDefs: ColDef[]): ColDef[] =>
    colDefs.map((colDef) => {
      return {
        ...colDef,
        rowGroup: colDef.field === groupBy,
        enableRowGroup: colDef.field === groupBy,
      };
    });
